import { HTTP_REQUEST_TYPE, REPORTS_API } from "../constants/api";
import { sendHttpRequest } from "./api";
import {
  CarReportApi,
  CarReportHistoryApi,
  CarReportAverageApi,
} from "../pages/reports/model/carReport.types";

export type CarSearchParams = {
  ufi?: string;
  comment?: string;
  page?: number;
  limit?: number;

  origin?: string;
  dest?: string;
  category?: string;
  subject?: string;
  creator?: string;
  assigneeLead?: string;
  assigneeGroup?: string;
  status?: string;

  sortBy?: string;
  sortOrder?: "asc" | "desc";
};

export type CarReportsPagedData = {
  records: CarReportApi[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

export type CarReportsResponse =
  | {
    code: number;
    msg: string;
    data: CarReportApi[];
  }
  | {
    code: number;
    msg: string;
    data: CarReportsPagedData;
  };

export const normalizeCarReportsResponse = (resp: any): {
  code: number;
  msg: string;
  records: CarReportApi[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
} => {
  const code = resp?.code ?? -1;
  const msg = resp?.msg ?? "";

  const data =
    resp?.data?.records
      ? resp.data
      : resp?.data?.data?.records
        ? resp.data.data
        : resp?.data;

  if (data && Array.isArray(data.records)) {
    const d = data as CarReportsPagedData;
    const records = d.records || [];

    const page = Number.isFinite(d.page) && d.page > 0 ? d.page : 1;
    const limit =
      Number.isFinite(d.limit) && d.limit > 0 ? d.limit : records.length;

    const total =
      Number.isFinite(d.total) && d.total >= 0 ? d.total : records.length;

    const totalPages =
      Number.isFinite(d.totalPages) && d.totalPages > 0 ? d.totalPages : 1;

    return { code, msg, records, page, limit, total, totalPages };
  }

  if (Array.isArray(data)) {
    const arr = data as CarReportApi[];
    return {
      code,
      msg,
      records: arr,
      page: 1,
      limit: arr.length,
      total: arr.length,
      totalPages: 1,
    };
  }

  return { code, msg, records: [], page: 1, limit: 0, total: 0, totalPages: 1 };
};

export const getCarReports = async (params: CarSearchParams) => {
  const query: any = {
    ufi: params.ufi,
    comment: params.comment,
    page: params.page,
  };

  if (params.limit !== undefined) query.limit = params.limit;
  if (params.sortBy) query.sortBy = params.sortBy;
  if (params.sortOrder) query.sortOrder = params.sortOrder;

  const addIfNonEmpty = (k: string, v?: string) => {
    const s = String(v ?? "").trim();
    if (s) query[k] = s;
  };

  addIfNonEmpty("origin", params.origin);
  addIfNonEmpty("dest", params.dest);
  addIfNonEmpty("category", params.category);
  addIfNonEmpty("subject", params.subject);
  addIfNonEmpty("creator", params.creator);
  addIfNonEmpty("assigneeLead", params.assigneeLead);
  addIfNonEmpty("assigneeGroup", params.assigneeGroup);
  addIfNonEmpty("status", params.status);

  const [, resp] = await sendHttpRequest(
    HTTP_REQUEST_TYPE.get,
    REPORTS_API.SEARCH_REPORTS,
    query,
  );

  return resp as CarReportsResponse;
};

export type CarReportFilterOptionsData = {
  origin: string[];
  dest: string[];
  category: string[];
  subject: string[];
  creator: string[];
  assigneeLead: string[];
  assigneeGroup: string[];
  status: string[];
};

export type CarReportFilterOptionsResponse = {
  code: number;
  msg: string;
  data: CarReportFilterOptionsData;
};

export const getCarReportFilterOptions = async () => {
  const [, resp] = await sendHttpRequest(
    HTTP_REQUEST_TYPE.get,
    REPORTS_API.FILTER_OPTIONS,
    {}
  );
  return resp as CarReportFilterOptionsResponse;
};

export interface CarReportHistoryResponse {
  code: number;
  msg: string;
  data: CarReportHistoryApi;
}

export interface CarReportAverageDayResponse {
  code: number;
  msg: string;
  data: CarReportAverageApi;
}

export const getCarReportHistory = async (carReportId: string) => {
  const [, resp] = await sendHttpRequest(
    HTTP_REQUEST_TYPE.get,
    REPORTS_API.GET_HISTORY,
    { carReportId },
  );
  return resp as CarReportHistoryResponse;
};

export type CreateCarReportPayload = {
  ufi: string;
  category: string;
  subject: string;
  comments: string;
  creator: string;
  airport?: string;
  chartRef?: string;
  isResponseRequired?: boolean;
  photoIDs?: string[];
  assigneeLead?: string;
  assigneeGroup?: string[];
};

type CreateCarReportRequestPayload = {
  ufi: string;
  category: string;
  subject: string;
  comments: string;
  creator: string;
  airport?: string;
  chartRef?: string;
  isResponseRequired: boolean;
  photoIDs?: string[];
  assigneeLead?: string;
  assigneeGroup?: string[];
};

const normalizeOptionalString = (value?: string): string | undefined => {
  const normalized = String(value ?? "").trim();
  return normalized || undefined;
};

const normalizeStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => String(item ?? "").trim())
    .filter(Boolean);
};

const normalizeBoolean = (value: unknown, fallback = false): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }
  return fallback;
};

export const createCarReportRecord = async (params: CreateCarReportPayload) => {
  const normalizedAssigneeGroup = normalizeStringArray(params.assigneeGroup);
  const normalizedAssigneeLead = normalizeOptionalString(params.assigneeLead);
  const normalizedPhotoIDs = normalizeStringArray(params.photoIDs);
  const payload: CreateCarReportRequestPayload = {
    ufi: String(params.ufi ?? "").trim(),
    category: String(params.category ?? "").trim(),
    subject: String(params.subject ?? "").trim(),
    comments: String(params.comments ?? "").trim(),
    creator: String(params.creator ?? "").trim(),
    isResponseRequired: normalizeBoolean(params.isResponseRequired, false),
    airport: normalizeOptionalString(params.airport),
    chartRef: normalizeOptionalString(params.chartRef),
    photoIDs: normalizedPhotoIDs.length > 0 ? normalizedPhotoIDs : undefined,
    assigneeGroup: normalizedAssigneeGroup.length > 0 ? normalizedAssigneeGroup : undefined,
    assigneeLead: normalizedAssigneeGroup.length === 0 ? normalizedAssigneeLead : undefined,
  };

  const [, resp] = await sendHttpRequest(
    HTTP_REQUEST_TYPE.post,
    REPORTS_API.RECORD_CREATE,
    payload,
  );
  return resp;
};

export interface AttachmentPayload {
  fileId: string;
  name: string;
}

export type RemarkAttachmentInput = string | AttachmentPayload;

export interface CreateRemarkParams {
  carReportId: string;
  content: string;
  author: string;
  attachment?: RemarkAttachmentInput[];
}

const normalizeAttachmentArray = (value?: RemarkAttachmentInput[]): AttachmentPayload[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((attachment) => {
      if (typeof attachment === "string") {
        const fileId = attachment.trim();
        if (!fileId) return null;
        return {
          fileId,
          name: fileId,
        } as AttachmentPayload;
      }
      const fileId = String(attachment?.fileId ?? "").trim();
      if (!fileId) return null;
      const name = String(attachment?.name ?? "").trim() || fileId;
      return {
        fileId,
        name,
      } as AttachmentPayload;
    })
    .filter(Boolean) as AttachmentPayload[];
};

export const createCarReportRemark = async (params: CreateRemarkParams) => {
  const normalizedAttachment = normalizeAttachmentArray(params.attachment);
  const payload = {
    carReportId: String(params.carReportId ?? "").trim(),
    content: String(params.content ?? "").trim(),
    author: String(params.author ?? "").trim(),
    attachment: normalizedAttachment.length > 0 ? normalizedAttachment : undefined,
  };

  const [, resp] = await sendHttpRequest(
    HTTP_REQUEST_TYPE.post,
    REPORTS_API.RECORD_REMARK,
    payload,
  );
  return resp;
};

export interface StatusRemarkPayload {
  content: string;
  attachment?: AttachmentPayload[];
}

export interface ChangeStatusParams {
  carReportId: string;
  status: string;
  author: string;
  remark?: StatusRemarkPayload;
}

export const changeCarReportStatus = async (params: ChangeStatusParams) => {
  const [, resp] = await sendHttpRequest(
    HTTP_REQUEST_TYPE.post,
    REPORTS_API.RECORD_STATUS,
    params,
  );
  return resp;
};

export type UpdateCarReportAssigneePayload = {
  carReportId: string;
  author: string;
  assigneeLead?: string;
  assigneeGroup?: string[];
};

export const updateCarReportAssignee = async (params: UpdateCarReportAssigneePayload) => {
  const [, resp] = await sendHttpRequest(
    HTTP_REQUEST_TYPE.post,
    REPORTS_API.ASSIGNEE_UPDATE,
    params,
  );
  return resp;
};

export const getAvgCaseTime = async () => {
  const query: any = {
    days: 30,
  }
  
  const [, resp] = await sendHttpRequest(
    HTTP_REQUEST_TYPE.get,
    REPORTS_API.COMPLETE_CASE_AVERAGE,
    query,
  );

  return resp as CarReportAverageDayResponse;
};
